import { useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import type {
  ConversationProgressDocument,
  ConversationProgressFieldType,
  ConversationProgressSemanticOutcome,
  ConversationProgressStep,
  ConversationProgressStepState,
  ConversationTurn,
} from '../../types';

/** Read-side state returned for one response's durable progress document. */
export interface UseConversationProgressResult {
  /** Latest durable progress, or null for legacy and unavailable rows. */
  progress: ConversationProgressDocument | null;
  /** Latest response transcript read alongside progress for evidence navigation. */
  conversationTurns: ConversationTurn[];
  /** True after a progress-only read or subscription failure. */
  unavailable: boolean;
}

/** Convert connector failures into a stable Error for telemetry. */
function progressError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error('Conversation progress read failed');
  }
}

const PROGRESS_MODES = new Set(['legacy', 'shadow', 'active']);
const PROGRESS_STATES = new Set<ConversationProgressStepState>([
  'pending',
  'active',
  'completed',
  'skipped',
  'failed',
]);
/**
 * Every field type this build can label precisely. Keyed by the full union so
 * the compiler flags any FieldType added without updating the admin read side.
 */
const PROGRESS_FIELD_TYPE_KEYS: Readonly<Record<ConversationProgressFieldType, true>> = {
  free_form_explore: true,
  information: true,
  calendar_booking: true,
  file_upload: true,
  short_text: true,
  number: true,
  email: true,
  phone: true,
  name: true,
  choice_single: true,
  choice_multi: true,
  checkbox: true,
  image_choice: true,
  ranking: true,
  date_time: true,
  matrix: true,
  address: true,
  signature: true,
  image_click_map: true,
  unsupported: true,
};
const PROGRESS_FIELD_TYPES: ReadonlySet<string> = new Set(Object.keys(PROGRESS_FIELD_TYPE_KEYS));
const PROGRESS_OUTCOMES = new Set<ConversationProgressSemanticOutcome>([
  'answered',
  'partial',
  'uncertain',
  'declined',
  'unable',
  'off_topic',
  'interrupted',
  'invalid',
  'requested_end',
]);
const CONVERSATION_SPEAKERS = new Set<ConversationTurn['speaker']>([
  'user',
  'ai',
  'system-prompt',
  'system-confirmation',
  'system-image-generation',
  'system-quote-saved',
]);

interface ParsedConversationProgress {
  progress: ConversationProgressDocument | null;
  error: Error | null;
}

/** Structurally valid persisted step whose field type may be newer than this build. */
type PersistedProgressStep = Omit<ConversationProgressStep, 'fieldType'> & {
  /** Worker-authored field type, possibly one this admin build does not know yet. */
  fieldType: string;
};

/** Structurally valid persisted document before field types are normalized. */
type PersistedProgressDocument = Omit<ConversationProgressDocument, 'steps'> & {
  /** Validated steps awaiting field-type normalization. */
  steps: PersistedProgressStep[];
};

/** Database columns that must agree with a non-null progress document. */
export interface ConversationProgressEnvelope {
  /** Database-level document format version. */
  conversation_progress_version?: unknown;
  /** Database-level compare-and-set revision. */
  conversation_progress_revision?: unknown;
  /** Fingerprint of the immutable response schema. */
  schema_fingerprint?: unknown;
  /** Session-latched engine mode. */
  conversation_engine_mode?: unknown;
}

const SUPPORTED_PROGRESS_FORMAT_VERSION = 1;

/** Narrow an unknown database JSON value before the admin card can read it. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Validate one transcript turn before using it as a navigation target. */
function isConversationTurn(value: unknown): value is ConversationTurn {
  if (!isRecord(value)) return false;
  return (
    CONVERSATION_SPEAKERS.has(value.speaker as ConversationTurn['speaker']) &&
    typeof value.text === 'string' &&
    (value.timestamp === undefined || (typeof value.timestamp === 'number' && Number.isFinite(value.timestamp))) &&
    (value.functionCallId === undefined || typeof value.functionCallId === 'string') &&
    (value.turn_id === undefined || typeof value.turn_id === 'string') &&
    (value.finalized_at === undefined || (typeof value.finalized_at === 'number' && Number.isFinite(value.finalized_at))) &&
    (value.interrupted === undefined || typeof value.interrupted === 'boolean')
  );
}

/**
 * Validate one public progress step while allowing the staggered stepId
 * fallback. Any non-blank field type is structurally valid: a worker deployed
 * ahead of this build may persist a type this build cannot label yet.
 */
function isPersistedProgressStep(value: unknown): value is PersistedProgressStep {
  if (!isRecord(value)) return false;
  const fieldId = typeof value.fieldId === 'string' ? value.fieldId.trim() : '';
  const stepId = typeof value.stepId === 'string' ? value.stepId.trim() : '';
  return (
    Boolean(fieldId || stepId) &&
    typeof value.fieldName === 'string' &&
    typeof value.fieldType === 'string' &&
    value.fieldType.trim().length > 0 &&
    Number.isInteger(value.schemaPosition) &&
    Number(value.schemaPosition) >= 0 &&
    typeof value.displayLabel === 'string' &&
    PROGRESS_STATES.has(value.state as ConversationProgressStepState) &&
    (
      value.semanticOutcome === undefined ||
      PROGRESS_OUTCOMES.has(value.semanticOutcome as ConversationProgressSemanticOutcome)
    ) &&
    Array.isArray(value.evidenceTurnIds) &&
    value.evidenceTurnIds.every((turnId) => typeof turnId === 'string') &&
    (value.skipReason === undefined || typeof value.skipReason === 'string') &&
    (value.failureReason === undefined || typeof value.failureReason === 'string') &&
    (value.reasonCode === undefined || typeof value.reasonCode === 'string')
  );
}

/** Validate the versioned public document shape and every nested step. */
function isPersistedProgressDocument(value: unknown): value is PersistedProgressDocument {
  if (!isRecord(value)) return false;
  return (
    Number.isInteger(value.formatVersion) &&
    Number(value.formatVersion) >= 1 &&
    typeof value.schemaFingerprint === 'string' &&
    PROGRESS_MODES.has(value.mode as string) &&
    (
      value.activeStepIndex === null ||
      (Number.isInteger(value.activeStepIndex) && Number(value.activeStepIndex) >= 0)
    ) &&
    Number.isInteger(value.revision) &&
    Number(value.revision) >= 0 &&
    typeof value.createdAt === 'string' &&
    typeof value.updatedAt === 'string' &&
    Array.isArray(value.steps) &&
    value.steps.every(isPersistedProgressStep) &&
    !(
      typeof value.activeStepIndex === 'number' &&
      value.activeStepIndex >= value.steps.length
    )
  );
}

/** Whether a persisted field type is one this build can label precisely. */
function isKnownProgressFieldType(fieldType: string): fieldType is ConversationProgressFieldType {
  return PROGRESS_FIELD_TYPES.has(fieldType);
}

/**
 * Read a field type this build does not know as unsupported, so one newer
 * step keeps its generic presentation instead of hiding the whole document.
 */
function normalizeProgressDocument(document: PersistedProgressDocument): ConversationProgressDocument {
  return {
    ...document,
    steps: document.steps.map((step) => ({
      ...step,
      fieldType: isKnownProgressFieldType(step.fieldType) ? step.fieldType : 'unsupported',
    })),
  };
}

/** Return a typed document only after every field the card reads has been checked. */
function parseConversationProgress(
  value: unknown,
  envelope: ConversationProgressEnvelope,
): ParsedConversationProgress {
  if (value === null || value === undefined) {
    return { progress: null, error: null };
  }
  if (!isPersistedProgressDocument(value)) {
    return {
      progress: null,
      error: new Error('Invalid conversation progress payload'),
    };
  }
  if (value.formatVersion !== SUPPORTED_PROGRESS_FORMAT_VERSION) {
    return {
      progress: null,
      error: new Error('Unsupported conversation progress format'),
    };
  }

  let revisionMatches = false;
  try {
    revisionMatches = BigInt(envelope.conversation_progress_revision as bigint) === BigInt(value.revision);
  } catch {
    revisionMatches = false;
  }
  if (
    envelope.conversation_progress_version !== value.formatVersion ||
    !revisionMatches ||
    envelope.schema_fingerprint !== value.schemaFingerprint ||
    envelope.conversation_engine_mode !== value.mode
  ) {
    return {
      progress: null,
      error: new Error('Conversation progress envelope mismatch'),
    };
  }
  return { progress: normalizeProgressDocument(value), error: null };
}

/**
 * Keep one response's progress document current without coupling failures to
 * the rest of the response page.
 */
export function useConversationProgress(
  responseId: string,
  initialProgress?: unknown,
  initialConversationTurns: ConversationTurn[] = [],
  initialEnvelope: ConversationProgressEnvelope = {},
): UseConversationProgressResult {
  const { supabase } = useSupabase();
  const parsedInitialProgress = useMemo(
    () => parseConversationProgress(initialProgress, initialEnvelope),
    [
      initialEnvelope.conversation_engine_mode,
      initialEnvelope.conversation_progress_revision,
      initialEnvelope.conversation_progress_version,
      initialEnvelope.schema_fingerprint,
      initialProgress,
    ],
  );
  const [progress, setProgress] = useState<ConversationProgressDocument | null>(
    parsedInitialProgress.progress,
  );
  const [conversationTurns, setConversationTurns] = useState(initialConversationTurns);
  const [unavailable, setUnavailable] = useState(Boolean(parsedInitialProgress.error));
  const fetchSequence = useRef(0);

  useEffect(() => {
    setProgress(parsedInitialProgress.progress);
    setConversationTurns(initialConversationTurns);
    setUnavailable(Boolean(parsedInitialProgress.error));
    if (parsedInitialProgress.error) {
      captureException(
        parsedInitialProgress.error,
        'useConversationProgress.parse',
        { responseId },
      );
    }
  }, [initialConversationTurns, parsedInitialProgress, responseId]);

  useEffect(() => {
    let cancelled = false;

    /** Fetch progress with its current transcript snapshot for stable evidence links. */
    const fetchProgress = async () => {
      const sequence = ++fetchSequence.current;
      try {
        const { data, error } = await supabase
          .from('interview_data')
          .select('conversation_progress, conversation_progress_version, conversation_progress_revision, conversation_engine_mode, schema_fingerprint, conversation_turns')
          .eq('id', responseId)
          .maybeSingle();

        if (cancelled || sequence !== fetchSequence.current) return;
        if (error) {
          captureException(progressError(error), 'useConversationProgress.fetch', { responseId });
          setUnavailable(true);
          return;
        }

        const parsedProgress = parseConversationProgress(data?.conversation_progress, {
          conversation_progress_version: data?.conversation_progress_version,
          conversation_progress_revision: data?.conversation_progress_revision,
          schema_fingerprint: data?.schema_fingerprint,
          conversation_engine_mode: data?.conversation_engine_mode,
        });
        if (parsedProgress.error) {
          captureException(
            parsedProgress.error,
            'useConversationProgress.fetch',
            { responseId },
          );
          setProgress(null);
          setUnavailable(true);
          return;
        }
        setProgress(parsedProgress.progress);
        if (data?.conversation_turns !== undefined && data.conversation_turns !== null) {
          if (
            Array.isArray(data.conversation_turns) &&
            data.conversation_turns.every(isConversationTurn)
          ) {
            setConversationTurns(data.conversation_turns);
          } else {
            captureException(
              new Error('Invalid conversation turns payload'),
              'useConversationProgress.fetchTranscript',
              { responseId },
            );
          }
        }
        setUnavailable(false);
      } catch (error) {
        if (cancelled || sequence !== fetchSequence.current) return;
        captureException(progressError(error), 'useConversationProgress.fetch', { responseId });
        setUnavailable(true);
      }
    };

    void fetchProgress();
    const channel = supabase
      .channel(`conversation_progress:${responseId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'respondent_sessions',
          filter: `id=eq.${responseId}`,
        },
        () => {
          void fetchProgress();
        },
      )
      .subscribe((status) => {
        if (cancelled) return;
        if (status === 'SUBSCRIBED') {
          void fetchProgress();
          return;
        }
        if (status !== 'CHANNEL_ERROR' && status !== 'TIMED_OUT' && status !== 'CLOSED') return;
        fetchSequence.current += 1;
        captureException(
          new Error(`Conversation progress subscription ${status.toLowerCase()}`),
          'useConversationProgress.subscribe',
          { responseId },
        );
        setUnavailable(true);
      });

    return () => {
      cancelled = true;
      fetchSequence.current += 1;
      void supabase.removeChannel(channel);
    };
  }, [responseId, supabase]);

  return { progress, conversationTurns, unavailable };
}

export default useConversationProgress;
